import type { ReactNode } from 'react'
import { Link } from '@tanstack/react-router'
import { useManufacturer } from '@/api/hooks'
import { useWarehousePurchaseOrder, type WarehousePurchaseOrder } from '@/api/warehouse-sourcing'
import { Card, CardContent } from '@/components/ui/card'
import { POShipToEditor } from '@/components/po-ship-to-editor'
import { cn, formatDateOnly } from '@/lib/utils'
import type { PurchaseOrderDetail } from './types'
import { FreightResponsibilityControl } from './freight'
import { POInstructionsPanel } from './settings'
import { POSourceControl } from './source'
import { careOfSupplier } from './source-model'

function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <>
      <dt className="pt-px text-xs text-muted-foreground">{label}</dt>
      <dd className="min-w-0 break-words font-medium">{children}</dd>
    </>
  )
}

/** The PO that took this one's place at another supplier, or the one it replaced (USA-1689). */
function POLink({ po }: { po: { uuid: string; po_number: string } }) {
  return (
    <Link to="/purchase-orders/$poId" params={{ poId: po.uuid }} className="text-primary hover:underline">
      {po.po_number}
    </Link>
  )
}

/** Where a warehouse PO's units sit, lot by lot: bay and pallet first, as the picker walks them. */
function WarehousePickFrom({ warehouse }: { warehouse: WarehousePurchaseOrder }) {
  return (
    <section aria-label="Pull from" className="min-w-0 space-y-1">
      <h4 className="flex h-6 items-center text-xs text-muted-foreground">Pull from</h4>
      <div className="space-y-0.5 break-words text-sm">
        <p className="font-medium">{warehouse.warehouse_name}</p>
        {warehouse.address && <p>{warehouse.address}</p>}
      </div>
      {warehouse.picks?.length ? (
        <ul className="space-y-1.5 pt-2 text-sm">
          {warehouse.picks.map((pick) => (
            <li key={pick.stock_uuid} className="min-w-0">
              <p className={cn('font-medium tabular-nums', pick.shipped && 'text-muted-foreground')}>
                {[pick.bay && `Bay ${pick.bay}`, pick.pallet && `Pallet ${pick.pallet}`]
                  .filter(Boolean)
                  .join(' · ') || 'No location'}
                {pick.shipped && <span className="font-normal"> · Shipped</span>}
              </p>
              <p className="text-xs text-muted-foreground">
                {pick.quantity} × {pick.model} · {pick.condition_label}
              </p>
            </li>
          ))}
        </ul>
      ) : null}
    </section>
  )
}

/** Who the PO goes to and where it ships, the order's terms, and its instructions. */
export function PODetailsCard({
  po,
  shipToName,
}: {
  po: PurchaseOrderDetail
  /** Ship-to name rendered as a link to the customer, when known. */
  shipToName?: ReactNode
}) {
  const manufacturer = po.manufacturer
  // Pulled from our own warehouse: where to pick it, instead of a supplier (USA-1574).
  const warehouse = useWarehousePurchaseOrder(po.uuid).data
  const fromWarehouse = warehouse?.from_warehouse === true
  // Bought through a distributor or retailer: show who we buy from, as the PO does.
  const sources = useManufacturer(manufacturer?.uuid ?? '').data?.sourcing_options ?? []
  const careOf = careOfSupplier(po, sources)
  const address = careOf ? careOf.address : manufacturer
  const cityLine = address
    ? [address.city, address.state].filter(Boolean).join(', ') +
      (address.zip_code ? ` ${address.zip_code}` : '')
    : ''

  return (
    <Card className="gap-0 py-0">
      <CardContent className="space-y-4 p-4">
        <h3 className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
          Details
        </h3>

        <div className="grid grid-cols-2 gap-x-4 gap-y-3">
          {fromWarehouse ? (
            <WarehousePickFrom warehouse={warehouse} />
          ) : manufacturer && (
            <section aria-label="Manufacturer" className="min-w-0 space-y-1">
              <h4 className="flex h-6 items-center text-xs text-muted-foreground">Manufacturer</h4>
              <div className="space-y-0.5 break-words text-sm">
                <Link
                  to="/manufacturers/$manuId"
                  params={{ manuId: manufacturer.uuid }}
                  className="font-medium text-primary hover:underline"
                >
                  {manufacturer.short_name}
                </Link>
                {careOf && <p className="font-medium">c/o {careOf.name}</p>}
                {address?.address1 && <p>{address.address1}</p>}
                {address?.address2 && careOf && <p>{address.address2}</p>}
                {cityLine.trim() && <p>{cityLine}</p>}
                {address?.phone && <p className="text-muted-foreground">{address.phone}</p>}
              </div>
            </section>
          )}
          <POShipToEditor po={po} effectiveName={shipToName} embedded />
        </div>

        <dl className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-4 gap-y-2 border-t pt-4 text-sm">
          {manufacturer && !fromWarehouse && <POSourceControl po={po} />}
          {po.replaced_by && (
            <Fact label="Replaced by">
              <POLink po={po.replaced_by.replacement_purchase_order} />
            </Fact>
          )}
          {po.replaces && (
            <Fact label="Replaces">
              <POLink po={po.replaces.replaced_purchase_order} />
            </Fact>
          )}
          {po.manufacturer_order_number && (
            <Fact label="Manufacturer order #">
              <span className="break-all">{po.manufacturer_order_number}</span>
            </Fact>
          )}
          <FreightResponsibilityControl
            poUuid={po.uuid}
            weBookFreight={po.we_book_freight}
            freightBilling={po.freight_billing ?? null}
            freightMode={po.freight_mode ?? null}
            estimatedWeightLbs={po.estimated_weight_lbs ?? null}
            estimatedPackageFacts={po.estimated_package_facts ?? {}}
            accessorialSnapshot={po.dropship_accessorial_snapshot ?? {}}
          />
          {po.freight_note && <Fact label="Freight note">{po.freight_note}</Fact>}
          {po.date_required && (
            <Fact label="Date required">{formatDateOnly(po.date_required)}</Fact>
          )}
          {po.ship_via && <Fact label="Ship via">{po.ship_via}</Fact>}
          {po.terms && <Fact label="Terms">{po.terms}</Fact>}
        </dl>

        <div className="space-y-3 border-t pt-4">
          <POInstructionsPanel poUuid={po.uuid} value={po.instructions ?? null} kind="po" embedded />
          <POInstructionsPanel
            poUuid={po.uuid}
            value={po.shipping_instructions ?? null}
            kind="shipping"
            embedded
          />
        </div>
      </CardContent>
    </Card>
  )
}
